import Component from "vue-class-component";
import Vue from "vue";
import {Watch} from "vue-property-decorator";

import Property from "@/components/properties/model/Property";
import {AXIOS} from "@/lib/http-common";
import UserActionResultSender from "@/components/application/UserActionResultSender";
import {CameraKey} from "@/components/properties/settings/camera/CameraKey";

@Component
export default class CameraSettingsView extends Vue {
  public loading: boolean = false;
  public testing: boolean = false;

  public databaseCameraSettings!: Property[];

  public cameraEnabled: boolean = false;
  public cameraIpAddress: string = '';
  public cameraIpPort: string = '80';
  public cameraAutoAfterMeasurement: boolean = true;
  public cameraAutoAfterBarcodeScan: boolean = false;
  public cameraDelayBeforeCapture: number = 0;
  public camera2DEnabled: boolean = false;
  public destinationFolder: string = "";
  public defaultImagesPath: string = "";
  public showCameraLiveModal = false;
  public cameraConnected = false;
  public mjpegStreamUrl = '';
  public liveCaptureUrl = "";
  public captureUrl = "";
  public axisUsername = "";
  public axisPassword = "";
  public authorise: boolean = false;


  public mounted() {
    this.getCameraSettingsFromDatabase();
  }

  public getPropertyValue(key: string): string {
    const setting = this.databaseCameraSettings.find((prop) => prop.propKey === key);
    return setting ? setting.propValue : "";
  }

  @Watch("cameraAutoAfterMeasurement")
  public onCameraAutoAfterMeasurementChange(newValue: boolean) {
    if (newValue) {
      this.cameraAutoAfterBarcodeScan = false;
    }
  }

  @Watch("cameraAutoAfterBarcodeScan")
  public onCameraAutoAfterBarcodeScanChange(newValue: boolean) {
    if (newValue) {
      this.cameraAutoAfterMeasurement = false;
    }
  }

  public setDefaultCameraProperties() {
    this.cameraEnabled = this.getPropertyValue(CameraKey.CAMERA_ENABLED) === "true";
    this.cameraIpPort = this.getPropertyValue(CameraKey.CAMERA_IP_PORT);
    this.cameraIpAddress = this.getPropertyValue(CameraKey.CAMERA_IP_ADRESS);
    this.cameraAutoAfterBarcodeScan = this.getPropertyValue(CameraKey.CAMERA_AUTO_AFTER_BARCODE_SCAN) === "true";
    this.cameraAutoAfterMeasurement = this.getPropertyValue(CameraKey.CAMERA_AUTO_AFTER_MEASUREMENT) === "true";
    this.destinationFolder = this.getPropertyValue(CameraKey.DESTINATION_FOLDER);
    this.cameraDelayBeforeCapture = Number(this.getPropertyValue(CameraKey.CAMERA_DELAY_BEFORE_CAPTURE));
    this.camera2DEnabled = this.getPropertyValue(CameraKey.IMAGE_2D_ENABLED) === "true";
    this.axisUsername = this.getPropertyValue(CameraKey.AXIS_USERNAME);
    this.axisPassword = this.getPropertyValue(CameraKey.AXIS_PASSWORD);
    this.liveCaptureUrl = this.getPropertyValue(CameraKey.CAMERA_LIVE_VIDEO_URL);
    this.captureUrl = this.getPropertyValue(CameraKey.CAMERA_CAPTURE_URL);
    this.authorise = this.getPropertyValue(CameraKey.AUTHORIZE) === "true";
  }

  public save() {
    const cameraSettings = [
      {propKey: CameraKey.CAMERA_ENABLED.valueOf(), propValue: this.cameraEnabled},
      {propKey: CameraKey.CAMERA_IP_ADRESS.valueOf(), propValue: this.cameraIpAddress},
      {propKey: CameraKey.CAMERA_IP_PORT.valueOf(), propValue: this.cameraIpPort},
      {propKey: CameraKey.CAMERA_AUTO_AFTER_MEASUREMENT.valueOf(), propValue: this.cameraAutoAfterMeasurement},
      {propKey: CameraKey.CAMERA_AUTO_AFTER_BARCODE_SCAN.valueOf(), propValue: this.cameraAutoAfterBarcodeScan},
      {
        propKey: CameraKey.DESTINATION_FOLDER.valueOf(),
        propValue: this.destinationFolder !== '' ? this.destinationFolder : this.defaultImagesPath
      },
      {propKey: CameraKey.CAMERA_DELAY_BEFORE_CAPTURE.valueOf(), propValue: this.cameraDelayBeforeCapture},
      {propKey: CameraKey.IMAGE_2D_ENABLED.valueOf(), propValue: this.camera2DEnabled},
      {propKey: CameraKey.AXIS_USERNAME.valueOf(), propValue: this.axisUsername},
      {propKey: CameraKey.AXIS_PASSWORD.valueOf(), propValue: this.axisPassword},
      {propKey: CameraKey.CAMERA_LIVE_VIDEO_URL.valueOf(), propValue: this.liveCaptureUrl},
      {propKey: CameraKey.CAMERA_CAPTURE_URL.valueOf(), propValue: this.captureUrl},
      {propKey: CameraKey.AUTHORIZE.valueOf(), propValue: this.authorise},
    ];

    AXIOS.put(`/properties/camera/save-list`, cameraSettings)
      .then((message) => {
        this.databaseCameraSettings = message.data;
        this.setDefaultCameraProperties();
        UserActionResultSender.showSuccessFeedback("camera.update.success");
      })
      .catch(error => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public getCameraSettingsFromDatabase() {
    this.loading = true;
    this.getDefaultDestinationFolder();
    AXIOS.post(`/properties/camera`, Object.values(CameraKey))
      .then((message) => {
        this.databaseCameraSettings = message.data;
        this.setDefaultCameraProperties();
      })
      .catch(error => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public getDefaultDestinationFolder() {
    this.loading = true;
    AXIOS.get('/properties/camera/default-folder')
      .then((message) => {
        this.defaultImagesPath = message.data;
      })
      .catch(error => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  get ipValidationRule() {
    return (value: string) => {
      const ipRegex = /^(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])$/;
      return ipRegex.test(value) || this.$t("camera.error.invalidIp");
    };
  }

  get portValidationRule() {
    return (value: string) => {
      const portRegex = /^(6553[0-5]|655[0-2]\d|65[0-4]\d{2}|6[0-4]\d{3}|[1-5]\d{4}|\d{1,4})$/;
      return portRegex.test(value) || this.$t("camera.error.invalidPort");
    };
  }

  public testConnection() {
    this.loading = true;
    return AXIOS.get(`/device/test-camera-connection`, {params: {address: this.getCameraAddressAndPort()}})
      .then((message) => {
        if (message.data) {
          this.cameraConnected = true;
          return;
        }
        UserActionResultSender.showErrorFeedback('camera.test.testError');
      })
      .catch(error => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public getCameraAddressAndPort() {
    return this.cameraIpAddress.concat(":").concat(this.cameraIpPort);
  }

  public async getLiveCamera() {
    await this.testConnection();
    if (this.cameraConnected) {
      this.mjpegStreamUrl = '/frontend-api/item/camera/live';
      this.showCameraLiveModal = true;
    }
  }

  public closeCameraLiveModal() {
    this.showCameraLiveModal = false;
    this.mjpegStreamUrl = '';
  }

}
